import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import type { UserAnalytics } from '../../types/dashboard';

interface AnalyticsChartProps {
  analytics: UserAnalytics;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ analytics }) => {
  // Add fallback values to prevent undefined errors
  const safeAnalytics = {
    monthlyActivity: analytics?.monthlyActivity || [],
    averageBooksPerMonth: analytics?.averageBooksPerMonth || 0,
    averageResourcesPerMonth: analytics?.averageResourcesPerMonth || 0,
    totalBooksBorrowed: analytics?.totalBooksBorrowed || 0,
    totalResourcesDownloaded: analytics?.totalResourcesDownloaded || 0,
    topDownloaded: analytics?.topDownloaded || [],
    topViewed: analytics?.topViewed || [],
    topBorrowingUsers: analytics?.topBorrowingUsers || [],
    ...analytics
  };

  // Format monthly activity data
  const monthlyData = safeAnalytics.monthlyActivity?.map(month => ({
    month: month.month,
    books: month.booksBorrowed || 0,
    resources: month.resourcesAccessed || 0,
    lessonPlans: month.lessonPlansSubmitted || 0
  })) || [];

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">
            {safeAnalytics.averageBooksPerMonth?.toFixed(1) || '0.0'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trung bình sách/tháng
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="secondary">
            {safeAnalytics.averageResourcesPerMonth?.toFixed(1) || '0.0'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trung bình tài nguyên/tháng
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main">
            {safeAnalytics.totalBooksBorrowed || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tổng sách đã mượn
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="warning.main">
            {safeAnalytics.totalResourcesDownloaded || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tổng tài nguyên đã tải
          </Typography>
        </Paper>
      </Box>

      {/* Charts in vertical order: Monthly Activity */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Monthly Activity Chart - Row 1 */}
        <Card>
          <CardHeader title="Thống Kê Hoạt Động Theo Tháng" />
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="books" fill="#8884d8" name="Sách đã mượn" />
                  <Bar dataKey="resources" fill="#82ca9d" name="Tài nguyên đã truy cập" />
                  <Bar dataKey="lessonPlans" fill="#ffc658" name="Giáo án đã nộp" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Chưa có dữ liệu hoạt động
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* TOP 5 Charts Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h5" gutterBottom>
            Thống Kê TOP 5
          </Typography>
          <Grid container spacing={3}>
            {/* TOP 5 Most Downloaded Resources */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    TOP 5 Sách Tải Nhiều Nhất
                  </Typography>
                  {safeAnalytics.topDownloaded && safeAnalytics.topDownloaded.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={safeAnalytics.topDownloaded.map(item => ({
                          ...item,
                          title: item.title.length > 30 ? item.title.substring(0, 30) + '...' : item.title
                        }))} 
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="title" 
                          type="category" 
                          width={150}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value: number) => [`${value} lượt tải`, '']}
                          labelFormatter={(label) => safeAnalytics.topDownloaded?.find(item => 
                            (item.title.length > 30 ? item.title.substring(0, 30) + '...' : item.title) === label
                          )?.title || label}
                        />
                        <Bar dataKey="count" fill="#8884d8" name="Lượt tải" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      Chưa có dữ liệu
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* TOP 5 Most Viewed Resources */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    TOP 5 Sách Xem Nhiều Nhất
                  </Typography>
                  {safeAnalytics.topViewed && safeAnalytics.topViewed.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={safeAnalytics.topViewed.map(item => ({
                          ...item,
                          title: item.title.length > 30 ? item.title.substring(0, 30) + '...' : item.title
                        }))} 
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="title" 
                          type="category" 
                          width={150}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value: number) => [`${value} lượt xem`, '']}
                          labelFormatter={(label) => safeAnalytics.topViewed?.find(item => 
                            (item.title.length > 30 ? item.title.substring(0, 30) + '...' : item.title) === label
                          )?.title || label}
                        />
                        <Bar dataKey="count" fill="#82ca9d" name="Lượt xem" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      Chưa có dữ liệu
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* TOP 5 Most Borrowing Users */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    TOP 5 Người Dùng Mượn Nhiều Nhất
                  </Typography>
                  {safeAnalytics.topBorrowingUsers && safeAnalytics.topBorrowingUsers.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={safeAnalytics.topBorrowingUsers.map(item => ({
                          ...item,
                          displayName: item.fullName.length > 20 ? item.fullName.substring(0, 20) + '...' : item.fullName
                        }))} 
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="displayName" 
                          type="category" 
                          width={150}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value: number) => [`${value} lượt mượn`, '']}
                          labelFormatter={(label) => safeAnalytics.topBorrowingUsers?.find(item => 
                            (item.fullName.length > 20 ? item.fullName.substring(0, 20) + '...' : item.fullName) === label
                          )?.fullName || label}
                        />
                        <Bar dataKey="count" fill="#ffc658" name="Lượt mượn" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      Chưa có dữ liệu
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
